/**
 * translate.ts
 * Traducción automática usando MyMemory (API gratuita, sin cuenta).
 * Con email en .env obtiene 10.000 palabras/día de límite.
 */

type Lang = 'en' | 'pt' | 'fr' | 'it' | 'de';

const LANGS: Lang[] = ['en', 'pt', 'fr', 'it', 'de'];
const EMAIL = import.meta.env.VITE_TRANSLATE_EMAIL || '';

/**
 * Traduce un texto de español al idioma destino usando MyMemory.
 * Retorna el texto original si la traducción falla.
 */
async function translateText(text: string, targetLang: Lang): Promise<string> {
    if (!text || text.trim() === '') return '';
    try {
        const emailParam = EMAIL ? `&de=${encodeURIComponent(EMAIL)}` : '';
        const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=es|${targetLang}${emailParam}`;
        const res = await fetch(url);
        const json = await res.json();
        if (json?.responseStatus === 200 && json?.responseData?.translatedText) {
            return json.responseData.translatedText;
        }
        return text; // fallback silencioso
    } catch {
        return text; // fallback silencioso
    }
}

/**
 * Traduce un array de strings de ES a un idioma destino.
 * Une con separador para minimizar llamadas a la API.
 */
async function translateArray(arr: string[], targetLang: Lang): Promise<string[]> {
    if (!arr || arr.length === 0) return [];
    // Traducir de a uno para evitar problemas con el separador
    const results = await Promise.all(arr.map(item => translateText(item, targetLang)));
    return results;
}

/**
 * Traduce los campos de texto de un itinerario a un idioma destino.
 * Campos técnicos (distance, time, day) se copian sin traducir.
 */
async function translateItinerary(items: any[], targetLang: Lang): Promise<any[]> {
    if (!items || items.length === 0) return [];
    return Promise.all(
        items.map(async (item) => {
            const [title, desc, hito, route] = await Promise.all([
                item.title ? translateText(item.title, targetLang) : Promise.resolve(''),
                item.desc  ? translateText(item.desc, targetLang)  : Promise.resolve(''),
                item.hito  ? translateText(item.hito, targetLang)  : Promise.resolve(''),
                item.route ? translateText(item.route, targetLang) : Promise.resolve(''),
            ]);
            return { ...item, title, desc, hito, route };
        })
    );
}

/**
 * Función principal: recibe los datos en español y devuelve un objeto
 * con todos los campos traducidos a EN, PT, FR, IT, DE listos para guardar en Supabase.
 */
export async function autoTranslateExpedition(
    data: any,
    onProgress?: (lang: Lang) => void
): Promise<Record<string, any>> {
    const result: Record<string, any> = {};

    // Traducir un idioma a la vez para poder reportar progreso
    for (const lang of LANGS) {
        const [
            name,
            desc,
            catchphrase,
            best_season,
            includes,
            excludes,
            recommendations,
            policies,
            itinerary,
        ] = await Promise.all([
            translateText(data.name_es || '', lang),
            translateText(data.desc_es || '', lang),
            data.catchphrase_es    ? translateText(data.catchphrase_es, lang)    : Promise.resolve(''),
            data.best_season_es    ? translateText(data.best_season_es, lang)    : Promise.resolve(''),
            translateArray(data.includes_es        || [], lang),
            translateArray(data.excludes_es        || [], lang),
            translateArray(data.recommendations_es || [], lang),
            translateArray(data.policies_es        || [], lang),
            translateItinerary(data.itinerary_es   || [], lang),
        ]);

        result[`name_${lang}`]            = name;
        result[`desc_${lang}`]            = desc;
        result[`catchphrase_${lang}`]     = catchphrase || null;
        result[`best_season_${lang}`]     = best_season || null;
        result[`includes_${lang}`]        = includes;
        result[`excludes_${lang}`]        = excludes;
        result[`recommendations_${lang}`] = recommendations;
        result[`policies_${lang}`]        = policies;
        result[`itinerary_${lang}`]       = itinerary;

        // Upgrades: traducir nombre de cada upgrade
        if (data.price_upgrades && data.price_upgrades.length > 0) {
            const names = data.price_upgrades.map((u: any) => u.name_es || '');
            const translatedNames = await translateArray(names, lang);
            result[`_upgrades_${lang}`] = translatedNames;
        }

        onProgress?.(lang);
    }

    // Reconstruir price_upgrades con todos los idiomas traducidos
    let mergedUpgrades = data.price_upgrades || [];
    if (mergedUpgrades.length > 0) {
        mergedUpgrades = mergedUpgrades.map((u: any, i: number) => {
            const merged = { ...u };
            for (const lang of LANGS) {
                const translatedNames = result[`_upgrades_${lang}`];
                if (translatedNames?.[i] !== undefined) {
                    merged[`name_${lang}`] = translatedNames[i];
                }
            }
            return merged;
        });
    }

    // Limpiar claves temporales
    for (const lang of LANGS) delete result[`_upgrades_${lang}`];

    return { ...result, price_upgrades: mergedUpgrades };
}

/**
 * Traduce un objeto de claves-valor de texto de español a un idioma destino usando MyMemory.
 */
export async function translateObject(obj: Record<string, any>, targetLang: Lang): Promise<Record<string, any>> {
    const result: Record<string, any> = {};
    const keys = Object.keys(obj);
    
    const translatedValues = await Promise.all(
        keys.map(async (key) => {
            const val = obj[key];
            if (typeof val === 'string' && val.trim() !== '') {
                return translateText(val, targetLang);
            }
            return val;
        })
    );
    
    keys.forEach((key, index) => {
        result[key] = translatedValues[index];
    });
    
    return result;
}
